import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { analyserSeance, formatTemps } from './seanceEngine.ts';
import { type ZoneRow } from './allureTable.ts';
import { enregistrerSeance, listerSeances, syntheseAEnregistrer, type Seance } from './seancesApi.ts';
import { classerSeance, typeSeance } from './classification.ts';
import { ajusterNotation, useAjustementRecup } from './ajustementRecup.ts';
import { aUnType, categorie as infoCategorie } from './composition.ts';
import { empreinteNotation } from './doublons.ts';
import { lireLigne, type LigneEchange } from './echangeSeances.ts';
import UploadFileIcon from '@mui/icons-material/UploadFile';

type Ligne = {
  notation: string;
  nom?: string;
  statut: 'valide' | 'erreur' | 'doublon';
  message: string;
  seance?: Seance;
};

/**
 * Import en lot de seances : une seance par ligne, au format d'echange
 * (cf echangeSeances.ts : "notation | nom | categorie | type | modele | notes",
 * seule la notation est obligatoire) - celui de l'export de la page Seances.
 * Chaque ligne est analysee avec la VMA et les allures courantes ; les lignes
 * valides et non deja enregistrees (meme suite d'etapes, cf doublons.ts) sont
 * creees (nom = notation par defaut, modifiable ensuite).
 */
export default function ImportSeances({
  ouvert,
  vma,
  table,
  onFermer,
  onImport,
}: {
  ouvert: boolean;
  vma: number;
  table: ZoneRow[];
  onFermer: () => void;
  /** Appele apres l'import (nombre de seances creees). */
  onImport: (nombre: number) => void;
}) {
  const [texte, setTexte] = React.useState('');
  const ajustementRecup = useAjustementRecup();
  const [existantes, setExistantes] = React.useState<Set<string>>(new Set());
  const [enCours, setEnCours] = React.useState(false);
  const [progression, setProgression] = React.useState(0);
  const [erreur, setErreur] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!ouvert) return;
    setErreur(null);
    setProgression(0);
    listerSeances()
      .then((liste: Seance[]) =>
        setExistantes(new Set(liste.map((s: Seance) => cleDe(s.notation)).filter((c): c is string => !!c))))
      .catch(() => setExistantes(new Set()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouvert, vma, table]);

  // Empreinte d'une notation (doublons : meme suite d'etapes, quelle que soit l'ecriture).
  function cleDe(notation: string): string | null {
    return empreinteNotation(notation, vma, table) ?? (notation.replace(/\s+/g, '').toLowerCase() || null);
  }

  // Analyse de chaque ligne (doublons : deja enregistree, ou repetee dans la saisie).
  const lignes = React.useMemo<Ligne[]>(() => {
    const vues = new Set<string>();
    return texte
      .split('\n')
      .map(lireLigne)
      .filter((l): l is LigneEchange => l !== null)
      .map(({ notation, nom, categorie, typeSeance: typeImporte, modeleSeance, notes }: LigneEchange): Ligne => {
        const cle = cleDe(notation) ?? notation;
        if (existantes.has(cle)) return { notation, nom, statut: 'doublon', message: 'déjà enregistrée' };
        if (vues.has(cle)) return { notation, nom, statut: 'doublon', message: 'en double dans la liste' };
        vues.add(cle);
        const resultat = analyserSeance(ajusterNotation(notation, ajustementRecup), vma, table);
        if (resultat.synthese.erreur) {
          return { notation, nom, statut: 'erreur', message: resultat.synthese.erreur };
        }
        const synthese = syntheseAEnregistrer(resultat.synthese, resultat.detail, vma);
        const typee = aUnType(categorie);
        const type = typee ? typeImporte ?? classerSeance(resultat.detail, table) : null;
        return {
          notation,
          nom,
          statut: 'valide',
          message: [
            infoCategorie(categorie).libelle,
            typeSeance(type)?.libelle,
            formatTemps(resultat.synthese.dureeTotaleS),
            `${(resultat.synthese.distanceTotaleM / 1000).toFixed(1)} km`,
            `RPE ${resultat.synthese.rpeSeance}`,
          ].filter(Boolean).join(' · '),
          seance: {
            nom: (nom ?? notation).substring(0, 100),
            notation,
            notes: notes ?? null,
            ...synthese,
            categorie,
            typeSeance: type,
            typeForce: typee && !!typeImporte,
            modeleSeance: modeleSeance ?? null,
          },
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texte, existantes, vma, table, ajustementRecup]);

  // Chargement d'un fichier exporte (.txt) dans la zone de saisie.
  const fichier = React.useRef<HTMLInputElement>(null);
  const lireFichier = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    f.text()
      .then((contenu) => setTexte(contenu))
      .catch(() => setErreur('Fichier illisible.'));
  };

  const aImporter = lignes.filter((l) => l.statut === 'valide');

  const importer = async () => {
    setEnCours(true);
    setErreur(null);
    let crees = 0;
    try {
      for (const ligne of aImporter) {
        await enregistrerSeance(ligne.seance as Seance);
        crees += 1;
        setProgression(crees);
      }
      setTexte('');
      onImport(crees);
      onFermer();
    } catch (e) {
      setErreur(`${crees} séance(s) importée(s), puis erreur : ${(e as Error).message}`);
      if (crees > 0) onImport(crees);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={ouvert} onClose={enCours ? undefined : onFermer} fullWidth maxWidth="md">
      <DialogTitle>Importer des séances</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Colle une séance par ligne avec la notation habituelle, ou charge un fichier exporté depuis la page Séances.
          Format complet : <code>notation | nom | catégorie | type | modèle | notes</code> — seule la notation est
          obligatoire (sans nom, la notation sert de nom). La synthèse est calculée avec ta VMA actuelle
          ({vma.toLocaleString('fr-FR')} km/h) ; les séances déjà enregistrées sont ignorées.
        </Typography>
        <Box sx={{ mb: 1 }}>
          <input ref={fichier} type="file" accept=".txt,text/plain" hidden onChange={lireFichier} />
          <Button size="small" startIcon={<UploadFileIcon />} onClick={() => fichier.current?.click()} disabled={enCours}>
            Charger un fichier
          </Button>
        </Box>
        <TextField
          multiline
          minRows={6}
          maxRows={14}
          fullWidth
          placeholder={"6x(2'@HP-R1')\n4x(6'@MP-R2') | Seuil 4x6' | CORPS\n20'@EASY | Échauffement 20' | ECHAUFFEMENT"}
          value={texte}
          onChange={(e) => setTexte(e.target.value)}
          disabled={enCours}
          InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
        />

        {lignes.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">
              {aImporter.length} à importer · {lignes.filter((l) => l.statut === 'erreur').length} invalide(s) ·{' '}
              {lignes.filter((l) => l.statut === 'doublon').length} ignorée(s)
            </Typography>
            <List dense sx={{ maxHeight: 260, overflow: 'auto' }}>
              {lignes.map((l, i) => (
                <ListItem key={i} disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {l.statut === 'valide' && <CheckCircleIcon fontSize="small" color="success" />}
                    {l.statut === 'erreur' && <ErrorIcon fontSize="small" color="error" />}
                    {l.statut === 'doublon' && <ContentCopyIcon fontSize="small" color="disabled" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={<>{l.nom && <b>{l.nom} — </b>}<code>{l.notation}</code></>}
                    secondary={l.message}
                    secondaryTypographyProps={{ color: l.statut === 'erreur' ? 'error' : 'text.secondary' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {enCours && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={(progression / Math.max(aImporter.length, 1)) * 100} />
            <Typography variant="caption">{progression} / {aImporter.length}</Typography>
          </Box>
        )}
        {erreur && <Alert severity="error" sx={{ mt: 2 }}>{erreur}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onFermer} disabled={enCours}>Annuler</Button>
        <Button variant="contained" onClick={importer} disabled={enCours || aImporter.length === 0}>
          Importer {aImporter.length > 0 ? `${aImporter.length} séance(s)` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
